import {
  ExtensionTag,
  NodeExtension,
  omitExtraAttributes,
  isNumber,
  getTextSelection,
} from '@remirror/core';
import { insertPoint } from '@remirror/pm/transform';
import { ExtensionImageTheme } from '@remirror/theme';

function createPlaceholder() {
  const element = document.createElement('div');
  element.classList.add(ExtensionImageTheme.IMAGE_LOADER);

  return element;
}

export class FileExtension extends NodeExtension {
  name = 'file';

  tags = [ExtensionTag.InlineNode];

  createNodeSpec(extra) {
    return {
      inline: true,
      attrs: {
        ...extra.defaults(),
        src: { default: null },
        name: { default: '' },
      },
      parseDOM: [{
        tag: 'span[data-ext="file"]',
        getAttrs: (node) => ({ ...extra.parse(node), name: node.textContent }),
      }],
      toDOM: (node) => {
        const attrs = omitExtraAttributes(node.attrs, extra);
        const { name, ...domAttrs } = extra.dom(node);
        return ['span', { ...domAttrs, ...attrs, 'data-ext': 'file' }, ['i', { class: 'icon file' }], name];
      },
    };
  }

  insertFile(attributes, selection) {
    return (param) => {
      const {
        tr,
        dispatch,
      } = param;
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);
      const node = this.type.create(attributes);
      dispatch(tr.replaceRangeWith(from, to, node));
      return true;
    };
  }

  createCommands() {
    return {
      insertFile: this.insertFile.bind(this),
      uploadFile: (
        value,
        onElement,
      ) => (props) => {
        const { tr } = props;

        // This is update in the validate hook
        let pos = tr.selection.from;
        return this.store
          .createPlaceholderCommand({
            promise: value,
            placeholder: {
              type: 'widget',
              get pos() {
                return pos;
              },
              createElement: (view, newPos) => {
                const element = createPlaceholder(view, newPos);
                onElement?.(element);
                return element;
              },
            },
            onSuccess: (file, range, commandProps) => this.insertFile(file, range)(commandProps),
          })
          .validate(({ tr: newTr, dispatch }) => {
            const insertPos = insertPoint(newTr.doc, pos, this.type);

            if (insertPos == null) {
              return false;
            }

            pos = insertPos;

            if (!newTr.selection.empty) {
              dispatch?.(newTr.deleteSelection());
            }

            return true;
          }, 'unshift')

          .generateCommand()(props);
      },
    };
  }

  fileUploadFileHandler(files, event, pos) {
    const { uploadHandler } = this.options;

    const { commands, chain } = this.store;
    const filesWithProgress = files.map((file, index) => ({
      file,
      progress: (progress) => {
        commands.updatePlaceholder(files[index], progress);
      },
    }));

    const uploads = uploadHandler(filesWithProgress);

    if (isNumber(pos)) {
      chain.selectText(pos);
    }

    for (const upload of uploads) { // eslint-disable-line no-restricted-syntax
      chain.uploadFile(upload);
    }

    chain.run();

    return true;
  }

  createPasteRules() {
    return [
      {
        type: 'file',
        fileHandler: (props) => {
          const pos = props.type === 'drop' ? props.pos : undefined;
          return this.fileUploadFileHandler(props.files, props.event, pos);
        },
      },
    ];
  }

  createPlugin() {
    return {
      props: {
        handleClick: (view, pos) => {
          const { doc } = view.state;
          // If span is clicked the parentOffset has to be resolved otherwise a node cannot be found
          const resolved = doc.resolve(pos);
          const node = doc.nodeAt(pos - resolved.parentOffset);
          if (!node) {
            return false;
          }
          if (node.type.name !== this.type.name) {
            return false;
          }

          if (!view.editable) {
            if (this.options.onClick) {
              this.options.onClick(node.attrs.src);
            }
          }

          return true;
        },
      },
    };
  }
}

export const createFileExtension = (options) => {
  const fileOptions = {
    extraAttributes: { src: { default: null }, name: { default: '' } },
    ...options[0],
  };
  return new FileExtension(fileOptions);
};
